'use client';
import { useState, useEffect } from 'react';
import { useAppStore } from '@/stores/app-store';
import { FIELD_META } from '@/lib/device-types';
import { Device, DashWidget, ScadaTank, ScadaPump } from '@/lib/types';
import { useMultiDeviceSeries } from '@/lib/use-device-series';
import { NO_VALUE } from '@/lib/device-status';

interface Props {
  widget: DashWidget;
  device?: Device;
}

type Shape = NonNullable<ScadaTank['shape']>;

const clampPct = (v: number | undefined): number => {
  if (typeof v !== 'number' || isNaN(v)) return 0;
  return Math.max(0, Math.min(100, v));
};

// Water colour tiers — high (cyan) / mid (amber) / low (red).
// 3 bands: ≥66% high (cyan) · 33–66% mid (amber) · <33% low (red).
const waterHex = (pct: number): string => (pct >= 66 ? '#22D3EE' : pct >= 33 ? '#FBBF24' : '#FB7185');

const num = (v: number | undefined): number => (typeof v === 'number' && !isNaN(v) ? v : 0);

// Bindings used when the widget carries no tanks/pumps config. height 100 /
// offset 0 makes pct === the raw reading, which is exactly what this widget did
// before it was configurable — existing dashboards must not shift.
const LEGACY_TANKS: ScadaTank[] = [
  { field: 'level_ground', shape: 'ground', height: 100 },
  { field: 'level_champ', shape: 'elevated', height: 100 },
];
const LEGACY_PUMPS: ScadaPump[] = [
  { field: 'pump1', ovlField: 'ovl1' },
  { field: 'pump2', ovlField: 'ovl2' },
];

interface Box { x: number; y: number; w: number; h: number }

// Slot geometry on the 680×340 viewBox (ground baseline y=300). The left slot
// always sits on the ground; the right slot rides its frame only when elevated.
const SLOT0_BOX: Box = { x: 34, y: 150, w: 150, h: 140 };
const SLOT0_UNDERGROUND: Box = { x: 40, y: 314, w: 170, h: 98 }; // buried below the y=300 baseline
const SLOT1_ELEVATED: Box = { x: 508, y: 72, w: 140, h: 124 };
const SLOT1_GROUNDED: Box = { x: 470, y: 150, w: 150, h: 140 };

const slotBox = (slot: number, shape: Shape): Box =>
  slot === 0 ? (shape === 'underground' ? SLOT0_UNDERGROUND : SLOT0_BOX)
    : shape === 'elevated' ? SLOT1_ELEVATED : SLOT1_GROUNDED;

// ── Tank outlines, all as a single path so one code path clips/strokes them ──
function roundRectPath(b: Box, r = 8): string {
  const x1 = b.x + b.w;
  const y1 = b.y + b.h;
  return `M${b.x + r} ${b.y} H${x1 - r} A${r} ${r} 0 0 1 ${x1} ${b.y + r} V${y1 - r} A${r} ${r} 0 0 1 ${x1 - r} ${y1} H${b.x + r} A${r} ${r} 0 0 1 ${b.x} ${y1 - r} V${b.y + r} A${r} ${r} 0 0 1 ${b.x + r} ${b.y} Z`;
}

// Champagne tank: dome top → straight sides → cone bottom.
function champagnePath(b: Box): string {
  const x1 = b.x + b.w;
  const cx = b.x + b.w / 2;
  const r = Math.round(b.w * 0.16);
  const shoulder = b.y + r;
  const waist = b.y + b.h * 0.63;
  return `M${b.x} ${shoulder} Q${b.x} ${b.y} ${b.x + r} ${b.y} H${x1 - r} Q${x1} ${b.y} ${x1} ${shoulder} V${waist} L${cx} ${b.y + b.h} L${b.x} ${waist} Z`;
}

// Vertical cylinder: elliptical caps top and bottom.
function cylinderPath(b: Box): string {
  const x1 = b.x + b.w;
  const y1 = b.y + b.h;
  const rx = b.w / 2;
  const ry = Math.max(6, Math.round(b.h * 0.07));
  return `M${b.x} ${b.y + ry} A${rx} ${ry} 0 0 1 ${x1} ${b.y + ry} V${y1 - ry} A${rx} ${ry} 0 0 1 ${b.x} ${y1 - ry} Z`;
}

const shapePath = (shape: Shape, b: Box): string =>
  shape === 'elevated'
    ? champagnePath(b)
    : shape === 'cylinder' || shape === 'underground'
      ? cylinderPath(b)
      : roundRectPath(b);

// One tank: outline, water body clipped to the outline, animated surface, and
// the percentage (plus the raw reading when a unit is configured).
function Tank({ id, box, shape, pct, label, sub, levelText, volText, missing }: {
  id: string; box: Box; shape: Shape; pct: number; label: string; sub?: string; levelText?: string; volText?: string;
  missing?: boolean; // no live reading (device offline) → "—", empty tank
}) {
  const d = shapePath(shape, box);
  const hex = waterHex(pct);
  const fillH = (box.h * pct) / 100;
  const fillY = box.y + box.h - fillH;
  const cx = box.x + box.w / 2;
  const textY = box.y + box.h * 0.4;
  const wave = (dy: number) =>
    `M${box.x} ${fillY} Q${box.x + box.w * 0.25} ${fillY + dy} ${box.x + box.w * 0.5} ${fillY} T${box.x + box.w} ${fillY} V${box.y + box.h} H${box.x} Z`;
  return (
    <g>
      <defs>
        <linearGradient id={`${id}-grad`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hex} stopOpacity="0.55" />
          <stop offset="100%" stopColor={hex} stopOpacity="0.9" />
        </linearGradient>
        <clipPath id={`${id}-clip`}>
          <path d={d} />
        </clipPath>
      </defs>
      <path d={d} fill="var(--bg-surface)" stroke="var(--text-secondary)" strokeWidth="2" />
      <g clipPath={`url(#${id}-clip)`}>
        {pct > 0 && (
          <>
            <rect x={box.x - 4} y={fillY} width={box.w + 8} height={fillH + 6} fill={`url(#${id}-grad)`} />
            {/* two surface waves at different periods → lively, visibly moving water */}
            <path d={wave(-9)} fill={hex} fillOpacity="0.45">
              <animate attributeName="d" dur="2.4s" repeatCount="indefinite"
                values={`${wave(-9)};${wave(9)};${wave(-9)}`} />
            </path>
            <path d={wave(7)} fill={hex} fillOpacity="0.28">
              <animate attributeName="d" dur="3.7s" repeatCount="indefinite"
                values={`${wave(7)};${wave(-7)};${wave(7)}`} />
            </path>
          </>
        )}
      </g>
      <path d={d} fill="none" stroke="var(--text-secondary)" strokeWidth="2" />
      {/* band dividers — split the level into 3 zones (ต่ำ / กลาง / สูง) */}
      {shape !== 'elevated' && [33, 66].map((m) => {
        const my = box.y + box.h - (box.h * m) / 100;
        return <line key={m} x1={box.x} y1={my} x2={box.x + box.w} y2={my} stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="4 4" opacity="0.4" />;
      })}
      <text x={cx} y={textY} textAnchor="middle" fontSize="28" fontWeight="800"
        fill={pct > 45 ? '#fff' : 'var(--text-primary)'}
        style={{ textShadow: pct > 45 ? '0 1px 4px rgba(0,0,0,.4)' : 'none' }}>
        {missing ? NO_VALUE : `${pct.toFixed(0)}%`}
      </text>
      {(() => {
        const info = pct > 45 ? 'rgba(255,255,255,.9)' : 'var(--text-secondary)';
        const lines = [levelText, volText].filter(Boolean) as string[];
        if (lines.length) return lines.map((t, i) => (
          <text key={i} x={cx} y={textY + 19 + i * 16} textAnchor="middle" fontSize="12.5" fontWeight="700" fill={info}>{t}</text>
        ));
        return sub ? <text x={cx} y={textY + 17} textAnchor="middle" fontSize="11" fontWeight="700" fill={info}>{sub}</text> : null;
      })()}
      <text x={cx} y={shape === 'underground' ? 293 : box.y - 8} textAnchor="middle" fontSize="12" fontWeight="700" fill="var(--text-secondary)">{label}</text>
    </g>
  );
}

// Support frame drawn under an elevated tank.
function TankFrame({ box }: { box: Box }) {
  const lt = box.x + 4;
  const rt = box.x + box.w - 6;
  const topY = box.y + box.h * 0.94;
  const lb = box.x - 8;
  const rb = box.x + box.w + 6;
  const beamY = box.y + box.h * 1.39;
  return (
    <g opacity="0.7">
      <path d={`M${lt} ${topY} L${lb} 300 M${rt} ${topY} L${rb} 300 M${lt} ${topY} L${rb} 300 M${rt} ${topY} L${lb} 300`}
        stroke="var(--text-secondary)" strokeWidth="2.5" fill="none" />
      <line x1={box.x - 2} y1={beamY} x2={box.x + box.w} y2={beamY} stroke="var(--text-secondary)" strokeWidth="2" opacity="0.85" />
    </g>
  );
}

// A centrifugal pump: casing + rotating impeller (when running) + status ring.
function Pump({ cx, cy, on, label }: { cx: number; cy: number; on: boolean; label: string }) {
  const color = on ? 'var(--green)' : 'var(--text-muted)';
  return (
    <g>
      <circle cx={cx} cy={cy} r="20" fill="var(--bg-elevated)" stroke={color} strokeWidth="2.5" />
      {on && <circle cx={cx} cy={cy} r="20" fill="none" stroke="var(--green)" strokeWidth="2.5" opacity="0.4">
        <animate attributeName="r" values="20;26;20" dur="1.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.4;0;0.4" dur="1.6s" repeatCount="indefinite" />
      </circle>}
      <g>
        {[0, 60, 120, 240, 300].map((d) => (
          <line key={d} x1={cx} y1={cy} x2={cx} y2={cy - 13} stroke={color} strokeWidth="2.4" strokeLinecap="round" transform={`rotate(${d} ${cx} ${cy})`} />
        ))}
        <circle cx={cx} cy={cy} r="3.5" fill={color} />
        {on && (
          <animateTransform attributeName="transform" type="rotate" from={`0 ${cx} ${cy}`} to={`360 ${cx} ${cy}`} dur="1.1s" repeatCount="indefinite" />
        )}
      </g>
      <text x={cx} y={cy + 33} textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--text-secondary)">{label}</text>
    </g>
  );
}

// Animated flow dashes along a path, shown only while liquid is moving.
function Flow({ d, active, color = '#22D3EE' }: { d: string; active: boolean; color?: string }) {
  if (!active) return null;
  return (
    <path d={d} fill="none" stroke={color} strokeWidth="3.4" strokeLinecap="round" strokeDasharray="2 12" opacity="0.95">
      <animate attributeName="stroke-dashoffset" from="0" to="-28" dur="0.8s" repeatCount="indefinite" />
    </path>
  );
}

const PUMP_CX = [250, 330];

// Water-usage summary panel (right of the schematic). Usage is ESTIMATED from the
// change in tank level (no flow meter). 7-day trend synthesised from today's value.
function UsagePanel({ th, inToday, inMonth, outToday, outMonth, trend7 }: {
  th: boolean; inToday: number; inMonth: number; outToday: number; outMonth: number; trend7?: number[];
}) {
  const fmt = (n: number) => Math.round(n).toLocaleString();
  // Real daily usage for the last 7 days (from level history); days before the
  // system had data show 0 — no synthetic history.
  const trend = (trend7 && trend7.length === 7) ? trend7 : new Array(7).fill(0);
  const tMax = Math.max(1, ...trend);
  // Real calendar date for a bar (0 = today, back to -6).
  const dLabel = (daysAgo: number) => new Date(Date.now() - daysAgo * 86400000).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit' });
  const Block = ({ title, color, today, month }: { title: string; color: string; today: number; month: number }) => (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 4 }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fmt(today)}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>ลบ.ม./วัน</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{th ? 'เดือนนี้' : 'This month'} <b style={{ color: 'var(--text-secondary)' }}>{fmt(month)}</b> ลบ.ม.</div>
    </div>
  );
  return (
    <div style={{ width: 'clamp(240px, 30%, 380px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8, overflowY: 'auto' }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-primary)' }}>💧 {th ? 'การใช้น้ำ' : 'Water Usage'}</div>
      <Block title={th ? 'น้ำเข้า · ถังใต้ดิน' : 'Inflow · Underground'} color="var(--blue)" today={inToday} month={inMonth} />
      <Block title={th ? 'น้ำจ่าย/ใช้ · ถังแชมเปญ' : 'Outflow/Use · Elevated'} color="var(--cyan)" today={outToday} month={outMonth} />
      {/* 7-day usage trend */}
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', borderRadius: 10, padding: '10px 12px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>{th ? 'แนวโน้มการใช้ 7 วัน' : 'Usage · 7 days'}</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 74 }}>
          {trend.map((v, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <span style={{ fontSize: 8, fontWeight: 700, color: i === trend.length - 1 ? 'var(--cyan)' : 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmt(v)}</span>
              <div style={{ width: '100%', height: `${Math.max(6, (v / tMax) * 46)}px`, borderRadius: '3px 3px 0 0', background: i === trend.length - 1 ? 'var(--cyan)' : 'color-mix(in srgb, var(--cyan) 45%, transparent)' }} />
              <span style={{ fontSize: 7.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{dLabel(trend.length - 1 - i)}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Customer-facing explanation of how the usage figures are derived */}
      <div style={{ fontSize: 9.5, color: 'var(--text-muted)', lineHeight: 1.55, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', borderRadius: 10, padding: '9px 11px' }}>
        <div style={{ fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>{th ? 'ℹ️ วิธีคำนวณการใช้น้ำ' : 'ℹ️ How water usage is calculated'}</div>
        {th ? (
          <>
            ประเมินจาก<b>การเปลี่ยนระดับน้ำในถัง</b> (ยังไม่มีมิเตอร์วัดการไหล):<br />
            • <b>น้ำเข้า</b> = ผลรวมช่วงที่ระดับ<b>ถังใต้ดินเพิ่มขึ้น</b> (น้ำจากประปา)<br />
            • <b>น้ำจ่าย/ใช้</b> = ผลรวมช่วงที่ระดับ<b>ถังแชมเปญลดลง</b> (น้ำที่ใช้ไป)<br />
            • ปริมาตร = (ระดับน้ำ ÷ ความสูงถังเต็ม) × ความจุถัง (ลบ.ม.)<br />
            • สรุปแยก “วันนี้” และ “เดือนนี้” จากข้อมูลระดับน้ำจริงย้อนหลัง<br />
            <span style={{ color: 'var(--amber)' }}><b>ความแม่นยำ:</b> เป็นค่าประเมินสำหรับดูแนวโน้ม (คลาดเคลื่อน ~10–20%) — จะต่ำกว่าจริงเมื่อน้ำเข้า-ออกพร้อมกัน หรือระดับเปลี่ยนน้อยกว่าความละเอียดเซนเซอร์ (ราย cm) · หากต้องการความแม่นยำสูง เช่น คิดค่าน้ำ/ตรวจสอบ แนะนำติด <b>มิเตอร์วัดการไหล (flow meter)</b> ที่แม่นระดับ ±1–2%</span>
          </>
        ) : (
          <>
            Estimated from <b>tank-level change</b> (no flow meter):<br />
            • <b>Inflow</b> = sum of periods the <b>underground tank rises</b> (mains in)<br />
            • <b>Outflow/Use</b> = sum of periods the <b>elevated tank falls</b> (water used)<br />
            • Volume = (level ÷ full tank height) × capacity (m³)<br />
            • Split into “today” and “this month” from real level history<br />
            <span style={{ color: 'var(--amber)' }}><b>Accuracy:</b> a trend estimate (~10–20% error) — under-reads when water flows in and out at once, or when the level moves less than the sensor’s cm resolution. For billing-grade accuracy install a <b>flow meter</b> (±1–2%).</span>
          </>
        )}
      </div>
      {/* Colour legend — explains the tank-fill levels, flow direction & pump state */}
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', borderRadius: 10, padding: '10px 12px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>{th ? 'ความหมายของสี' : 'Legend'}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 10.5, color: 'var(--text-secondary)' }}>
          {([
            ['#00E5A0', th ? 'ระดับน้ำปกติ (> 60%)' : 'Normal level (> 60%)'],
            ['#FFB347', th ? 'ระดับปานกลาง (30–60%)' : 'Medium (30–60%)'],
            ['#FF6B6B', th ? 'ระดับต่ำ — ควรเติมน้ำ (< 30%)' : 'Low — refill (< 30%)'],
            ['var(--blue)', th ? 'INFLOW · น้ำเข้าถัง (จากประปา)' : 'INFLOW · water in (mains)'],
            ['var(--cyan)', th ? 'OUTFLOW · จ่ายน้ำออกไปใช้' : 'OUTFLOW · water out'],
          ] as [string, string][]).map(([c, l]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: c, flexShrink: 0 }} />
              <span>{l}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: 'var(--green)', flexShrink: 0 }} />
            <span>{th ? 'ปั๊มเดิน' : 'Pump RUN'}</span>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: 'var(--text-muted)', flexShrink: 0, marginLeft: 8 }} />
            <span>{th ? 'ปั๊มหยุด' : 'Pump STOP'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ScadaWidget({ widget, device }: Props) {
  const lang = useAppStore((s) => s.lang);
  const th = lang === 'th';

  // Auto-refresh countdown (data is polled every 30s by WidgetRenderer's
  // useDeviceLatest; this ticker mirrors that cadence so users see it refresh).
  const [rc, setRc] = useState(30);
  useEffect(() => {
    const id = setInterval(() => setRc((s) => (s <= 1 ? 30 : s - 1)), 1000);
    return () => clearInterval(id);
  }, []);

  // When the usage panel is on (and there is no flow meter), estimate inflow/outflow
  // from the change in each tank's level over the last 30 days (level series → volume).
  const usageOn = !!(widget as unknown as { usage?: boolean }).usage;
  const uTanks = (widget.tanks || []).slice(0, 2);
  const usageSeries = useMultiDeviceSeries(
    uTanks.map((t) => ({ macId: device?.sn ?? '', keys: t.field })),
    '30d',
    usageOn && !!device?.sn,
  );

  if (!device) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)', fontSize: 13 }}>No device</div>;
  }

  const data = device.data || {};
  const offline = device.st === 'offline';
  const fn = (k: string, fallback: string) => device.fieldNames?.[k] || FIELD_META[k]?.label || fallback;

  // Config or legacy bindings. Slots are positional, so an entry with a blank
  // field (or shape 'none') just hides that slot rather than shifting the rest.
  const legacyTanks = !widget.tanks?.length;
  const legacyPumps = !widget.pumps?.length;
  const tankCfg = (legacyTanks ? LEGACY_TANKS : widget.tanks!).slice(0, 2);
  const pumpCfg = (legacyPumps ? LEGACY_PUMPS : widget.pumps!).slice(0, 2);

  const tanks = tankCfg.map((t, i) => {
    const shape: Shape = t.shape || (i === 0 ? 'ground' : 'elevated');
    let raw = num(data[t.field]);
    // The legacy ground slot accepted water_pct as a stand-in for level_ground.
    if (legacyTanks && i === 0 && raw === 0) raw = num(data.water_pct);
    // Offline (or the field never arrived): no reading — the tank shows "—"
    // and stays empty instead of a stale or 0% level.
    const missing = offline || (data[t.field] === undefined && !(legacyTanks && i === 0 && data.water_pct !== undefined));
    const offset = Number(t.offset ?? 0);
    const span = Number(t.height) - offset;
    // Sensors read depth from the tank floor: empty at `offset`, full at `height`.
    const pct = !missing && Number.isFinite(span) && span > 0 ? clampPct(((raw - offset) / span) * 100) : 0;
    // Level (m): prefer the sensor's REAL measured depth (raw reading in a length
    // unit → metres); fall back to pct × configured depth for %-only sensors.
    const depth = Number(t.depth), capacity = Number(t.capacity);
    const U = (t.unit || '').toLowerCase();
    const rawM = U === 'cm' ? raw / 100 : U === 'mm' ? raw / 1000 : U === 'm' ? raw : null;
    const levelText = missing ? undefined : rawM != null
      ? `ระดับ ${rawM.toFixed(2)} ม.`
      : (Number.isFinite(depth) && depth > 0 ? `ระดับ ${((pct / 100) * depth).toFixed(2)} ม.` : undefined);
    const volText = !missing && Number.isFinite(capacity) && capacity > 0 ? `${Math.round((pct / 100) * capacity).toLocaleString()} ลบ.ม.` : undefined;
    return {
      shape,
      pct,
      missing,
      visible: !!t.field && shape !== 'none',
      label: t.label || fn(t.field, i === 0 ? (th ? 'ถังพื้น' : 'Ground Tank') : (th ? 'ถังแชมเปญ' : 'Elevated Tank')),
      // Raw reading is only worth showing once a unit says what it means.
      sub: t.unit && !missing ? `${Number(raw.toFixed(1))} ${t.unit}` : undefined,
      levelText, volText,
    };
  });

  const pumps = pumpCfg.map((p, i) => ({
    // `invert` flips the sense for active-low inputs (0 = running). Read the
    // field first so a missing value stays "stopped" either way rather than
    // becoming "running" just because it is absent.
    on: !offline && (() => {
      const raw = data[p.field];
      if (raw === undefined) return false;
      return p.invert ? num(raw) < 1 : num(raw) >= 1;
    })(),
    trip: !offline && !!p.ovlField && num(data[p.ovlField]) >= 1,
    hasOvl: !!p.ovlField,
    visible: !!p.field,
    label: p.label || fn(p.field, th ? `ปั๊ม ${i + 1}` : `Pump ${i + 1}`),
  }));

  const src = tanks[0];
  const dest = tanks[1];
  const anyPumpOn = pumps.some((p) => p.visible && p.on);
  const showOvl = pumps.some((p) => p.visible && p.hasOvl);
  const anyTrip = pumps.some((p) => p.visible && p.trip);

  // Pipe geometry follows the destination slot: an elevated tank is fed high on
  // its shell and discharges from the cone, a grounded one is fed at its top.
  const destElevated = dest?.visible && dest.shape === 'elevated';
  const destBox = dest ? slotBox(1, dest.shape) : SLOT1_ELEVATED;
  // Underground source tank: sits below the baseline; the viewBox grows downward
  // and the suction risers up out of the ground into the pump header.
  const undergroundSrc = !!(src?.visible && src.shape === 'underground');
  const VBH = undergroundSrc ? 420 : 340;
  const suctionPath = undergroundSrc ? 'M125 320 V282 H340' : 'M120 282 H340';
  const dischargePath = destElevated ? 'M250 230 H400 V108 H510' : 'M250 230 H400 V170 H470';
  const supplyPath = destElevated ? 'M584 196 V250 H660' : 'M620 250 H660';

  // Water-usage panel (opt-in via widget.usage) — estimated from tank level change
  // (no flow meter). Reads inflow_/use_ fields; 7-day trend synthesised from today.
  // Prefer real flow-meter fields when present; otherwise estimate from level change.
  const fieldUsage = { inToday: num(data.inflow_today), inMonth: num(data.inflow_month), outToday: num(data.use_today), outMonth: num(data.use_month) };
  const hasFlowMeter = fieldUsage.inToday || fieldUsage.inMonth || fieldUsage.outToday || fieldUsage.outMonth;
  // Start-of-today in epoch ms (Asia/Bangkok).
  const nowBkk = new Date(Date.now() + 7 * 3600 * 1000);
  const startTodayMs = Date.UTC(nowBkk.getUTCFullYear(), nowBkk.getUTCMonth(), nowBkk.getUTCDate()) - 7 * 3600 * 1000;
  // Usage reset point (epoch ms): level samples before this are ignored so the
  // counters can be zeroed and start accumulating anew (set via "Reset usage").
  const usageEpoch = Number((widget as unknown as { usageEpoch?: number }).usageEpoch) || 0;
  // Sum the rising (inflow) and falling (outflow) volume deltas of one tank's level series.
  const flowsOf = (seriesIdx: number, t: ScadaTank | undefined) => {
    const r = usageSeries.data?.[seriesIdx];
    const ts = r?.timestamps ?? [];
    const d = r?.series?.[0]?.data ?? [];
    const full = Number(t?.height) || 100;
    const cap = Number(t?.capacity) || 0;
    const vol = (cm: number) => (cap > 0 ? Math.max(0, Math.min(1, cm / full)) * cap : 0);
    let inT = 0, inM = 0, outT = 0, outM = 0, prev: number | null = null;
    for (let i = 0; i < ts.length; i++) {
      if (usageEpoch && ts[i] < usageEpoch) continue; // before the reset point
      const raw = d[i]; if (raw == null) continue;
      const v = vol(raw);
      if (prev != null) {
        const dv = v - prev; const today = ts[i] >= startTodayMs;
        if (dv > 0.02) { inM += dv; if (today) inT += dv; }
        else if (dv < -0.02) { outM += -dv; if (today) outT += -dv; }
      }
      prev = v;
    }
    return { inT, inM, outT, outM };
  };
  const f0 = flowsOf(0, uTanks[0]); // underground (inflow source)
  const f1 = flowsOf(1, uTanks[1]); // elevated (outflow/use)
  const usage = usageOn
    ? (hasFlowMeter ? fieldUsage : { inToday: f0.inT, inMonth: f0.inM, outToday: f1.outT, outMonth: f1.outM })
    : null;
  // Real per-day usage for the last 7 days (elevated-tank falling deltas bucketed by
  // Bangkok calendar day). Days with no data stay 0 — no fabricated history.
  const bkkDayStart = (ms: number) => Math.floor((ms + 7 * 3600 * 1000) / 86400000) * 86400000 - 7 * 3600 * 1000;
  const dailyOut7 = (seriesIdx: number, t: ScadaTank | undefined): number[] => {
    const r = usageSeries.data?.[seriesIdx];
    const ts = r?.timestamps ?? []; const d = r?.series?.[0]?.data ?? [];
    const full = Number(t?.height) || 100; const cap = Number(t?.capacity) || 0;
    const vol = (cm: number) => (cap > 0 ? Math.max(0, Math.min(1, cm / full)) * cap : 0);
    const buckets = new Array(7).fill(0); // index 0 = 6 days ago … 6 = today
    let prev: number | null = null;
    for (let i = 0; i < ts.length; i++) {
      if (usageEpoch && ts[i] < usageEpoch) continue; // before the reset point
      const raw = d[i]; if (raw == null) continue; const v = vol(raw);
      if (prev != null) {
        const dv = v - prev;
        if (dv < -0.02) {
          const idx = 6 - Math.round((startTodayMs - bkkDayStart(ts[i])) / 86400000);
          if (idx >= 0 && idx < 7) buckets[idx] += -dv;
        }
      }
      prev = v;
    }
    return buckets;
  };
  const trend7 = usageOn && !hasFlowMeter ? dailyOut7(1, uTanks[1]) : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 240, gap: 6 }}>
      {/* ── Schematic (+ optional usage panel on the right) ── */}
      <div style={{ flex: 1, display: 'flex', gap: 12, minHeight: 0 }}>
      <div style={{ flex: 1, position: 'relative', minHeight: 180 }}>
        {offline && (
          <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: 'var(--red-dim)', color: 'var(--red)' }}>
            {th ? 'ออฟไลน์' : 'OFFLINE'}
          </div>
        )}
        {/* Auto-refresh indicator — data reloads every 30s */}
        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>🔄</span>
          {th ? `รีเฟรชใน ${rc} วิ` : `refresh in ${rc}s`}
        </div>
        <svg viewBox={`0 0 680 ${VBH}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '100%', opacity: offline ? 0.45 : 1 }}>
          {/* Soil layer below ground (only when a tank is buried underground) */}
          {undergroundSrc && (
            <rect x="0" y="300" width="680" height={VBH - 300} fill="var(--text-secondary)" opacity="0.10" />
          )}
          {/* Ground baseline */}
          <line x1="20" y1="300" x2="660" y2="300" stroke="var(--glass-border)" strokeWidth="2" />
          {[40, 90, 140, 190, 240, 290, 340, 390, 440, 490, 540, 590, 640].map((gx) => (
            <line key={gx} x1={gx} y1="300" x2={gx - 10} y2="310" stroke="var(--glass-border)" strokeWidth="1.5" opacity="0.5" />
          ))}

          {/* ── Municipal supply (ประปา) → underground tank = INFLOW ── */}
          {undergroundSrc && (
            <g>
              <rect x="20" y="150" width="104" height="30" rx="6" fill="var(--bg-elevated)" stroke="var(--blue)" strokeWidth="1.5" />
              <text x="72" y="170" textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--blue)">🚰 {th ? 'ประปา' : 'Mains'}</text>
              {/* down the left of the tank, then into the tank's left wall (side inlet) */}
              <path d="M32 180 V352 H64" fill="none" stroke="var(--text-muted)" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
              <Flow d="M32 180 V352 H64" active={!offline} />
              <path d="M60 345 L72 352 L60 359 Z" fill="var(--blue)" />
              {/* label sits beside the inflow riser on the left */}
              <g transform="translate(44, 236)">
                <rect x="0" y="-11" width="104" height="21" rx="10" fill="color-mix(in srgb, var(--blue) 16%, transparent)" />
                <text x="52" y="4" textAnchor="middle" fontSize="10.5" fontWeight="800" fill="var(--blue)">↓ INFLOW {th ? 'น้ำเข้า' : ''}</text>
              </g>
            </g>
          )}

          {/* ── Pipes (static) — each shown only when the slot it serves exists ── */}
          {src?.visible && (
            <path d={suctionPath} fill="none" stroke="var(--text-muted)" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
          )}
          {dest?.visible && (
            <>
              <path d={dischargePath} fill="none" stroke="var(--text-muted)" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
              <path d={supplyPath} fill="none" stroke="var(--text-muted)" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
            </>
          )}

          {/* ── Flow animation ── */}
          <Flow d={suctionPath} active={!!src?.visible && anyPumpOn && src.pct > 0} />
          <Flow d={dischargePath} active={!!dest?.visible && anyPumpOn && (src ? src.pct > 0 : true)} />
          <Flow d={supplyPath} active={!!dest?.visible && dest.pct > 2} />

          {/* ── Source tank (left slot) ── */}
          {src?.visible && (
            <>
              {src.shape === 'elevated' && <TankFrame box={slotBox(0, src.shape)} />}
              <Tank id="scada-src" box={slotBox(0, src.shape)} shape={src.shape} pct={src.pct} label={src.label} sub={src.sub} levelText={src.levelText} volText={src.volText} missing={src.missing} />
            </>
          )}

          {/* Suction risers from the shared header up into each pump */}
          {src?.visible && pumps.map((p, i) => p.visible && (
            <line key={i} x1={PUMP_CX[i]} y1="282" x2={PUMP_CX[i]} y2="262" stroke="var(--text-muted)" strokeWidth="7" strokeLinecap="round" opacity="0.55" />
          ))}

          {/* ── Pumps ── */}
          {pumps.map((p, i) => p.visible && (
            <Pump key={i} cx={PUMP_CX[i]} cy={250} on={p.on} label={p.label} />
          ))}

          {/* Overload indicators — only for pumps with an overload field bound */}
          {pumps.map((p, i) => p.visible && p.hasOvl && (
            <g key={i} transform={`translate(${PUMP_CX[i] + 24}, 234)`}>
              <rect x="0" y="0" width="34" height="15" rx="4" fill={offline ? 'var(--bg-elevated)' : p.trip ? 'var(--red-dim)' : 'var(--green-dim)'} />
              <text x="17" y="11" textAnchor="middle" fontSize="9" fontWeight="800" fill={offline ? 'var(--text-muted)' : p.trip ? 'var(--red)' : 'var(--green)'}>{offline ? NO_VALUE : p.trip ? 'TRIP' : 'OL✓'}</text>
            </g>
          ))}

          {/* ── Destination tank (right slot) ── */}
          {dest?.visible && (
            <>
              {dest.shape === 'elevated' && <TankFrame box={destBox} />}
              <Tank id="scada-dest" box={destBox} shape={dest.shape} pct={dest.pct} label={dest.label} sub={dest.sub} levelText={dest.levelText} volText={dest.volText} missing={dest.missing} />
            </>
          )}

          {/* ── Distribution endpoint (จ่ายเข้าอาคาร) = OUTFLOW ── */}
          {dest?.visible && (
            <g transform="translate(636, 250)">
              <rect x="0" y="0" width="34" height="42" rx="3" fill="var(--bg-elevated)" stroke="var(--text-secondary)" strokeWidth="1.5" />
              <rect x="13" y="6" width="8" height="8" rx="1.5" fill="var(--red)" />
              <rect x="15.5" y="4" width="3" height="12" fill="var(--bg-elevated)" />
              <rect x="11.5" y="8" width="11" height="3" fill="var(--bg-elevated)" />
            </g>
          )}
          {undergroundSrc && dest?.visible && (
            <g transform="translate(600, 228)">
              <rect x="0" y="-11" width="106" height="21" rx="10" fill="color-mix(in srgb, var(--cyan) 16%, transparent)" />
              <text x="53" y="4" textAnchor="middle" fontSize="10.5" fontWeight="800" fill="var(--cyan)">OUTFLOW {th ? 'จ่ายอาคาร' : ''} →</text>
            </g>
          )}
        </svg>
      </div>
        {usage && <UsagePanel th={th} {...usage} trend7={trend7} />}
      </div>

      {/* ── Status chip row — built from whatever slots are bound ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {tanks.map((t, i) => t.visible && (
          <Chip key={`t${i}`} label={t.label} value={t.missing ? NO_VALUE : `${t.pct.toFixed(0)}%`} color={t.missing ? 'var(--text-muted)' : waterHex(t.pct)} />
        ))}
        {pumps.map((p, i) => p.visible && (
          <Chip key={`p${i}`} label={p.label} value={offline ? NO_VALUE : p.on ? 'RUN' : 'STOP'} color={p.on ? 'var(--green)' : 'var(--text-muted)'} />
        ))}
        {showOvl && (
          <Chip label={th ? 'โอเวอร์โหลด' : 'Overload'} value={offline ? NO_VALUE : anyTrip ? 'TRIP' : 'OK'} color={offline ? 'var(--text-muted)' : anyTrip ? 'var(--red)' : 'var(--green)'} />
        )}
      </div>
    </div>
  );
}

function Chip({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ flex: '1 1 auto', minWidth: 78, padding: '5px 8px', borderRadius: 8, background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)', textAlign: 'center' }}>
      <div style={{ fontSize: 8, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}
